import { SpaceBetween } from '@cloudscape-design/components';
import Gauge from 'components/Gauge';
import { formatNumber } from 'features/plc/components/common';
import PLCNumberEditor from 'features/plc/components/PLCNumberEditor';
import { PLC_PATHS } from 'features/plc/contracts/pressHMI.schema';
import { usePLCValue } from 'features/plc/providers/PLCProvider';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

const TITLE_BY_SIDE = {
  upper: 'Upper Heater',
  lower: 'Lower Heater',
};

const roundDisplayTemp = (value) => (
  typeof value === 'number' ? Number(value.toFixed(2)) : 0
);

function TemperaturePanel({
  side,
  fields,
  title = null,
  children = null,
}) {
  const measuredTemp = usePLCValue(PLC_PATHS.status[`${side}ToolTempAvg`]);
  const target = usePLCValue(PLC_PATHS.status[`${side}AppliedSetpoint`]);
  const heatRateCpm = usePLCValue(PLC_PATHS.status[`${side}HeatBlock`].tempRateCpm);
  const outputPercent = usePLCValue(PLC_PATHS.status[`${side}HeatBlock`].outputPercent);
  const cylinderUp = usePLCValue(PLC_PATHS.status.pressInputs[`${side}CylinderEngageFeedback`]);
  const setpointMin = usePLCValue(PLC_PATHS.cmd[`${side}HeatZoneConfig`].setpointMin);
  const setpointMax = usePLCValue(PLC_PATHS.cmd[`${side}HeatZoneConfig`].setpointMax);

  const roundedTemp = roundDisplayTemp(measuredTemp);
  const roundedTarget = roundDisplayTemp(target);
  const boundedFields = useMemo(() => fields.map((field) => ({
    ...field,
    min: typeof setpointMin === 'number' ? setpointMin : field.min,
    max: typeof setpointMax === 'number' ? setpointMax : field.max,
  })), [fields, setpointMax, setpointMin]);

  const bands = useMemo(() => {
    if (roundedTarget === 0) {
      return [];
    }

    return [{
      id: 'target',
      start: roundedTarget - 5,
      end: roundedTarget + 5,
      color: 'positive',
    }];
  }, [roundedTarget]);

  return (
    <SpaceBetween size="s">
      <Gauge
        title={title ?? TITLE_BY_SIDE[side]}
        temp={roundedTemp}
        max={300}
        bands={bands}
        target={roundedTarget !== 0 ? roundedTarget : 'N/A'}
        heatRateCpm={formatNumber(heatRateCpm, ' C/min')}
        showIndicators
        heaterOn={outputPercent > 0}
        heaterText={outputPercent > 0 ? `Heater ${formatNumber(outputPercent, '%')}` : 'Heater Off'}
        cylinderUp={cylinderUp}
        cylinderText={cylinderUp ? 'Cooler On' : 'Cooler Off'}
      />
      <SpaceBetween size="xs">
        {boundedFields.map((field) => (
          <PLCNumberEditor
            key={field.path.name}
            field={field}
          />
        ))}
      </SpaceBetween>
      {children}
    </SpaceBetween>
  );
}

TemperaturePanel.propTypes = {
  side: PropTypes.oneOf(['upper', 'lower']).isRequired,
  fields: PropTypes.arrayOf(PropTypes.shape({
    description: PropTypes.string,
    label: PropTypes.string.isRequired,
    path: PropTypes.shape({
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    }).isRequired,
  })).isRequired,
  title: PropTypes.string,
  children: PropTypes.node,
};

export default TemperaturePanel;
